import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Play, Hotel, WashingMachine } from "lucide-react";
import PageShell from "@/components/opsiq/PageShell";
import Seo from "@/components/Seo";

const PRODUCTS = [
  {
    slug: "gida",
    name: "GIDA",
    kicker: "Hospitality",
    tagline: "Your stay. Your space. Your experience.",
    short:
      "A digital guest experience platform for hotels, guesthouses and short-stay homes — personal welcomes, loyalty rewards, in-room purchases and insight, all under your own brand.",
    Icon: Hotel,
    bg: "bg-[#0E1830]",
    accent: "#E7C66A",
    ink: "#F1ECE0",
    testid: "product-card-gida",
  },
  {
    slug: "washflow",
    name: "WashFlow",
    kicker: "Laundry operations",
    tagline: "Run your laundry smarter, from pickup to delivery.",
    short:
      "A laundry operations and intelligence platform for Ghana — track every garment, message every customer on WhatsApp, and see exactly how your business is doing from your phone.",
    Icon: WashingMachine,
    bg: "bg-[#F1ECE0]",
    accent: "#B8E024",
    ink: "#1A2E4A",
    testid: "product-card-washflow",
  },
];

export default function ProductsPage() {
  return (
    <>
      <Seo
        title="OpsIQ Products — GIDA & WashFlow"
        description="Software built on operational intelligence. GIDA (digital guest experience for hotels & short-stay homes) and WashFlow (laundry operations & intelligence for Ghana)."
        path="/products"
        image="/og-image.jpg?v=2"
        imageAlt="OpsIQ Products — GIDA and WashFlow"
      />
      <PageShell
      testid="products-page"
      eyebrow="Products"
      title={
        <>
          Software, built on <span className="italic">operational intelligence.</span>
        </>
      }
      subtitle="OpsIQ doesn't just advise. We build the software our clients actually run their businesses on. Here's what we ship today."
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {PRODUCTS.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                to={`/products/${p.slug}`}
                data-testid={p.testid}
                className={`group block relative overflow-hidden ${p.bg} min-h-[460px] md:min-h-[520px] p-8 md:p-10 flex flex-col justify-between`}
                style={{ color: p.ink }}
              >
                <div
                  className="absolute -right-10 -top-10 h-56 w-56 rounded-full opacity-[0.08] group-hover:opacity-20 transition-opacity"
                  style={{ background: p.accent }}
                />
                <div className="flex items-start justify-between relative">
                  <div
                    className="h-12 w-12 rounded-full flex items-center justify-center"
                    style={{ background: p.accent }}
                  >
                    <p.Icon size={22} strokeWidth={1.5} className="text-[#1A2E4A]" />
                  </div>
                  <div
                    className="text-[10px] font-plex-mono uppercase tracking-[0.22em] opacity-60"
                  >
                    {p.kicker}
                  </div>
                </div>

                <div className="relative mt-16 md:mt-24">
                  <div
                    className="font-editorial text-5xl md:text-6xl leading-[0.95] tracking-tight mb-5"
                    style={{ color: p.ink }}
                  >
                    {p.name}
                  </div>
                  <div
                    className="font-editorial text-xl md:text-2xl italic leading-tight mb-6"
                    style={{ color: p.accent }}
                  >
                    {p.tagline}
                  </div>
                  <p
                    className="text-[15px] leading-relaxed max-w-[46ch] opacity-80"
                    style={{ color: p.ink }}
                  >
                    {p.short}
                  </p>
                </div>

                <div className="relative mt-10 flex items-center gap-2 text-[11px] font-plex-mono uppercase tracking-[0.22em] group-hover:gap-4 transition-all">
                  <span>Explore {p.name}</span>
                  <ArrowUpRight size={14} />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 md:mt-24 border-t border-white/10 pt-10 md:pt-14 flex flex-col md:flex-row md:items-center gap-6 justify-between">
          <div className="max-w-xl">
            <div className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#D9FF3C] mb-3">
              Custom build
            </div>
            <h3 className="font-editorial text-3xl md:text-4xl leading-tight tracking-tight text-[#F1ECE0]">
              Need something tailored to your industry?
            </h3>
            <p className="mt-3 text-[#F1ECE0]/70">
              GIDA and WashFlow started as custom builds for OpsIQ clients. If you need a Command Centre or an operational
              product built for your business, let's talk.
            </p>
          </div>
          <Link
            to="/book"
            className="inline-flex items-center gap-2 px-6 py-4 bg-[#D9FF3C] text-[#142645] text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#E4FF66] transition-colors self-start"
          >
            Book a working session <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </PageShell>
    </>
  );
}
